'use client';
import React, { useMemo, type JSX } from 'react';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';

export type TextShimmerProps = {
  children: string;
  as?: React.ElementType;
  className?: string;
  duration?: number;
  spread?: number;
  delay?: number;
};

function TextShimmerComponent({
  children,
  as: Component = 'p',
  className,
  duration = 2,
  spread = 2,
  delay = 0,
}: TextShimmerProps) {
  const MotionComponent = motion.create(
    Component as keyof JSX.IntrinsicElements
  );

  const dynamicSpread = useMemo(() => {
    return children.length * spread;
  }, [children, spread]);

  return (
    <MotionComponent
      className={cn(
        'relative inline-block bg-[length:250%_100%,auto] bg-clip-text',
        'text-transparent [--base-color:#eaeaea] [--base-gradient-color:#ffffff]',
        'dark:[--base-color:#bdbdbd] dark:[--base-gradient-color:#ffffff]',
        '[background-repeat:no-repeat,padding-box] [--bg:linear-gradient(90deg,#0000_calc(50%-8%),var(--base-gradient-color),#0000_calc(50%+8%))]',
        'dark:[--bg:linear-gradient(90deg,#0000_calc(50%-8%),var(--base-gradient-color),#0000_calc(50%+8%))]',
        '[--shine-color:rgba(255,255,255,0.5)]',
        '[--bg:linear-gradient(90deg,#0000_calc(50%-8%),var(--shine-color),var(--base-gradient-color),var(--shine-color),#0000_calc(50%+8%))]',
        className
      )}
      initial={{ backgroundPosition: '100% center' }}
      animate={{ backgroundPosition: '0% center' }}
      transition={{
        repeat: Infinity,
        repeatType: 'loop',
        repeatDelay: delay,
        duration,
        ease: 'linear',
      }}
      style={
        {
          '--spread': `${dynamicSpread}px`,
          backgroundImage: `var(--bg), linear-gradient(var(--base-color), var(--base-color))`,
        } as React.CSSProperties
      }
    >
      {children}
    </MotionComponent>
  );
}

export const TextShimmer = React.memo(TextShimmerComponent);
